import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'ghost' | 'dark' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  // Navy label on orange: white on #F78D1E is ~2.4:1 (fails AA); navy is ~6:1.
  primary: 'bg-brand text-on-brand shadow-brand hover:bg-brand-hover border border-transparent',
  secondary: 'bg-surface text-navy border-[1.5px] border-line-strong shadow-xs hover:border-brand hover:text-brand-dark',
  ghost: 'bg-transparent text-brand-dark border border-transparent hover:bg-brand-tint',
  dark: 'bg-navy text-white border border-transparent shadow-sm hover:bg-navy-light',
  danger: 'bg-fail text-white border border-transparent hover:opacity-90',
};
const SIZES: Record<Size, string> = {
  sm: 'h-[34px] px-3.5 text-[0.8125rem] gap-1.5',
  md: 'h-11 px-5 text-[0.9375rem] gap-2',
  lg: 'h-[54px] px-7 text-[1.0625rem] gap-2.5',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  chevron?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', chevron = false, fullWidth = false, type = 'button', className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'group inline-flex items-center justify-center rounded-md font-heading font-bold leading-none tracking-[0.01em]',
        'transition-[background,border-color,color,transform] duration-[120ms] ease-out active:translate-y-px',
        'disabled:opacity-45 disabled:cursor-not-allowed disabled:shadow-none',
        VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className,
      )}
      {...rest}
    >
      {children}
      {chevron && <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" className="transition-transform duration-[120ms] group-hover:translate-x-0.5" />}
    </button>
  );
});
